import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, ArrowRightLeft, EyeOff, Clock, ZoomIn } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { API_BASE_URL } from '../utils/apiBase';
import * as localStore from '../services/localPlantStore';

const BACKEND_URL = API_BASE_URL;
// Show up to this many photos per plant in the banner — beyond that we
// just show a "+N" badge to keep the banner compact.
const PREVIEW_MAX_PER_PLANT = 3;
// Compare overlay pulls up to this many photos per side so the user
// can eyeball more angles than the compact banner shows.
const COMPARE_MAX_PER_PLANT = 5;

/**
 * A single duplicate-pair suggestion. Loads its own photo thumbnails
 * (blob URLs in Drive mode, data-URIs in local mode) so the user can
 * visually confirm the pair without opening the merge dialog first.
 * Two dismiss options:
 *   • "Not a duplicate" — permanent hide, never suggest again.
 *   • "Review later" — hides from the library banner; still surfaces
 *     in Settings ▸ Duplicate review for later triage.
 */
export const DuplicateSuggestionCard = ({
  target, source, pairKey, isLocalMode,
  onMerge, onDismissPermanent, onDismissReviewLater,
}) => {
  const { t } = useLanguage();
  const [targetThumbs, setTargetThumbs] = useState([]);
  const [sourceThumbs, setSourceThumbs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Compare overlay — opens full-screen side-by-side view when the user
  // taps any thumbnail. Fetches larger versions than the compact banner.
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTarget, setCompareTarget] = useState([]);
  const [compareSource, setCompareSource] = useState([]);
  const [compareLoading, setCompareLoading] = useState(false);
  // Active photo index per side inside the compare overlay — powers
  // the hero+strip picker so users can flip through multiple photos
  // per plant without getting a tiny grid.
  const [comparePhotoIdx, setComparePhotoIdx] = useState({ target: 0, source: 0 });
  // Compare overlay only — flips which plant is Kept vs Merged-in so
  // the user can pick either role before hitting Merge. Also drives
  // the mobile swipe-left/right gesture.
  const [compareSwapped, setCompareSwapped] = useState(false);
  const _touchStartRef = React.useRef(null);
  const onCompareTouchStart = useCallback((e) => {
    const tt = e.touches && e.touches[0];
    _touchStartRef.current = tt ? { x: tt.clientX, y: tt.clientY, t: Date.now() } : null;
  }, []);
  const onCompareTouchEnd = useCallback((e) => {
    const start = _touchStartRef.current;
    _touchStartRef.current = null;
    if (!start) return;
    const tt = (e.changedTouches && e.changedTouches[0]) || null;
    if (!tt) return;
    const dx = tt.clientX - start.x;
    const dy = tt.clientY - start.y;
    // Horizontal dominance + minimum 50 px travel + quicker than 800 ms.
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && Date.now() - start.t < 800) {
      setCompareSwapped((v) => !v);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        if (isLocalMode) {
          const [tp, sp] = await Promise.all([
            localStore.getAllPhotos(target.plant_id),
            localStore.getAllPhotos(source.plant_id),
          ]);
          if (cancelled) return;
          setTargetThumbs(tp.slice(0, PREVIEW_MAX_PER_PLANT).map(p => (
            p.thumbnail_base64
              ? `data:image/jpeg;base64,${p.thumbnail_base64}`
              : (p.image_base64 ? `data:image/jpeg;base64,${p.image_base64}` : null)
          )));
          setSourceThumbs(sp.slice(0, PREVIEW_MAX_PER_PLANT).map(p => (
            p.thumbnail_base64
              ? `data:image/jpeg;base64,${p.thumbnail_base64}`
              : (p.image_base64 ? `data:image/jpeg;base64,${p.image_base64}` : null)
          )));
        } else {
          const sessionToken = localStorage.getItem('auth_hint');
          const fetchThumb = async (did) => {
            if (!did) return null;
            try {
              const resp = await axios.get(`${BACKEND_URL}/api/photos/${did}`, {
                headers: { Authorization: `Bearer ${sessionToken}` },
                responseType: 'blob',
              });
              return URL.createObjectURL(resp.data);
            } catch { return null; }
          };
          const tIds = (target.photo_drive_ids || (target.photo_drive_id ? [target.photo_drive_id] : [])).slice(0, PREVIEW_MAX_PER_PLANT);
          const sIds = (source.photo_drive_ids || (source.photo_drive_id ? [source.photo_drive_id] : [])).slice(0, PREVIEW_MAX_PER_PLANT);
          const [tt, ss] = await Promise.all([
            Promise.all(tIds.map(fetchThumb)),
            Promise.all(sIds.map(fetchThumb)),
          ]);
          if (cancelled) return;
          setTargetThumbs(tt);
          setSourceThumbs(ss);
        }
      } catch (err) {
        console.warn('DuplicateSuggestionCard: thumb load failed:', err?.message || err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isLocalMode, target, source]);

  // Load full-size photos (or the largest we have) when the compare
  // overlay opens. Reuses the same fetch logic but pulls up to 5 per
  // side. Cached inside the component so re-opening is instant.
  const openCompare = useCallback(async () => {
    setCompareOpen(true);
    if (compareTarget.length > 0 || compareSource.length > 0) return; // already loaded
    setCompareLoading(true);
    try {
      if (isLocalMode) {
        const [tp, sp] = await Promise.all([
          localStore.getAllPhotos(target.plant_id),
          localStore.getAllPhotos(source.plant_id),
        ]);
        setCompareTarget(tp.slice(0, COMPARE_MAX_PER_PLANT).map(p => (
          p.image_base64
            ? `data:image/jpeg;base64,${p.image_base64}`
            : (p.thumbnail_base64 ? `data:image/jpeg;base64,${p.thumbnail_base64}` : null)
        )));
        setCompareSource(sp.slice(0, COMPARE_MAX_PER_PLANT).map(p => (
          p.image_base64
            ? `data:image/jpeg;base64,${p.image_base64}`
            : (p.thumbnail_base64 ? `data:image/jpeg;base64,${p.thumbnail_base64}` : null)
        )));
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const fetchFull = async (did) => {
          if (!did) return null;
          try {
            const resp = await axios.get(`${BACKEND_URL}/api/photos/${did}`, {
              headers: { Authorization: `Bearer ${sessionToken}` },
              responseType: 'blob',
            });
            return URL.createObjectURL(resp.data);
          } catch { return null; }
        };
        const tIds = (target.photo_drive_ids || (target.photo_drive_id ? [target.photo_drive_id] : [])).slice(0, COMPARE_MAX_PER_PLANT);
        const sIds = (source.photo_drive_ids || (source.photo_drive_id ? [source.photo_drive_id] : [])).slice(0, COMPARE_MAX_PER_PLANT);
        const [tt, ss] = await Promise.all([
          Promise.all(tIds.map(fetchFull)),
          Promise.all(sIds.map(fetchFull)),
        ]);
        setCompareTarget(tt);
        setCompareSource(ss);
      }
    } catch (err) {
      console.warn('Compare load failed:', err?.message || err);
    } finally {
      setCompareLoading(false);
    }
  }, [isLocalMode, target, source, compareTarget.length, compareSource.length]);

  // ESC / browser-back closes the overlay without dismissing the banner.
  useEffect(() => {
    if (!compareOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') { setCompareOpen(false); setCompareSwapped(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [compareOpen]);

  // Body scroll-lock while the overlay is open so vertical drags never
  // leak to the library page underneath (iter-89 testing found the page
  // behind was scrolling when the user tried to scroll the overlay).
  useEffect(() => {
    if (!compareOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [compareOpen]);

  const renderThumbRow = (plant, thumbs, isTarget) => {
    const totalPhotoCount = (plant.photo_drive_ids || (plant.photo_drive_id ? [plant.photo_drive_id] : [])).length;
    const extras = Math.max(0, (isLocalMode ? thumbs.length : totalPhotoCount) - PREVIEW_MAX_PER_PLANT);
    return (
      <div className="flex-1 min-w-0 space-y-1.5" data-testid={`dupe-preview-${isTarget ? 'target' : 'source'}`}>
        <div className="flex items-baseline gap-1.5">
          <span
            className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: isTarget ? 'hsl(143, 28%, 25%)' : 'hsl(21, 70%, 45%)',
              color: 'white',
            }}
          >
            {isTarget ? (t('mergeKeepThis') || 'Kept') : (t('mergeAbsorbed') || 'Merged in')}
          </span>
          <span className="text-xs font-medium truncate" style={{ color: 'hsl(143, 28%, 25%)' }}>
            {plant.common_name}
          </span>
        </div>
        <div className="flex gap-1">
          {thumbs.length === 0 && !loading ? (
            <div
              className="text-[10px] italic px-2 py-2 rounded"
              style={{ color: 'hsl(125, 12%, 45%)' }}
            >
              {t('dupePreviewNoPhotos') || 'No photos'}
            </div>
          ) : (
            thumbs.map((src, i) => (
              <button
                key={i}
                type="button"
                onClick={openCompare}
                className="w-10 h-10 rounded overflow-hidden flex-shrink-0 relative group"
                style={{
                  border: `1.5px solid ${isTarget ? 'hsl(143, 28%, 55%)' : 'hsl(21, 70%, 55%)'}`,
                  backgroundColor: 'hsl(143, 28%, 92%)',
                  padding: 0,
                  cursor: 'zoom-in',
                }}
                aria-label={t('dupeCompareOpen') || 'Compare side by side'}
                data-testid={`dupe-thumb-${isTarget ? 'target' : 'source'}-${i}`}
              >
                {src ? (
                  <img src={src} alt="" className="w-full h-full object-cover" draggable={false} />
                ) : null}
                <span
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
                  aria-hidden="true"
                >
                  <ZoomIn className="w-4 h-4" style={{ color: 'white' }} />
                </span>
              </button>
            ))
          )}
          {extras > 0 && (
            <div
              className="w-10 h-10 rounded flex items-center justify-center text-[10px] font-medium flex-shrink-0"
              style={{
                border: `1.5px dashed ${isTarget ? 'hsl(143, 28%, 55%)' : 'hsl(21, 70%, 55%)'}`,
                color: 'hsl(125, 12%, 45%)',
              }}
            >
              +{extras}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="rounded-lg p-3 space-y-3"
      style={{ backgroundColor: 'hsl(143, 28%, 96%)', border: '1px solid hsl(143, 28%, 82%)' }}
      data-testid={`duplicate-suggestion-${pairKey}`}
    >
      <div className="flex items-start gap-2">
        <span
          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: 'hsl(143, 28%, 88%)', color: 'hsl(143, 28%, 25%)' }}
        >
          {t('duplicateBadge') || 'Duplicate?'}
        </span>
        <p className="text-xs flex-1 min-w-0" style={{ color: 'hsl(143, 28%, 25%)' }}>
          {target.common_name === source.common_name
            ? (t('duplicateBannerBody') || 'Two entries share the name "{name}". Merge them?').replace('{name}', target.common_name)
            : (t('duplicateBannerBodyFuzzy') || '"{a}" and "{b}" look like the same plant. Merge them?')
                .replace('{a}', target.common_name)
                .replace('{b}', source.common_name)}
        </p>
      </div>

      {/* Photo previews of both plants side by side */}
      <div className="flex gap-3 items-start">
        {renderThumbRow(target, targetThumbs, true)}
        <ArrowRightLeft className="w-4 h-4 mt-6 flex-shrink-0" style={{ color: 'hsl(125, 12%, 45%)' }} />
        {renderThumbRow(source, sourceThumbs, false)}
      </div>

      {/* Actions: Merge (primary) + two dismiss options */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onMerge?.(target, source)}
          className="text-xs font-medium px-3 py-1.5 rounded-md flex-shrink-0 inline-flex items-center gap-1"
          style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
          data-testid={`duplicate-suggestion-merge-${pairKey}`}
        >
          <ArrowRightLeft className="w-3.5 h-3.5" />
          {t('mergeAction') || 'Merge'}
        </button>
        <button
          type="button"
          onClick={openCompare}
          className="text-xs px-3 py-1.5 rounded-md flex-shrink-0 inline-flex items-center gap-1"
          style={{
            color: 'hsl(143, 28%, 25%)',
            backgroundColor: 'white',
            border: '1px solid hsl(143, 28%, 55%)',
          }}
          data-testid={`duplicate-suggestion-compare-${pairKey}`}
          title={t('dupeCompareTip') || 'View both plants side-by-side before merging'}
        >
          <ZoomIn className="w-3.5 h-3.5" />
          {t('dupeCompare') || 'Compare'}
        </button>
        <button
          type="button"
          onClick={() => onDismissReviewLater?.(pairKey)}
          className="text-xs px-3 py-1.5 rounded-md flex-shrink-0 inline-flex items-center gap-1"
          style={{
            color: 'hsl(125, 12%, 35%)',
            backgroundColor: 'white',
            border: '1px solid hsl(143, 28%, 82%)',
          }}
          data-testid={`duplicate-suggestion-later-${pairKey}`}
          title={t('dupeReviewLaterTip') || 'Hide for now — review again later in Settings'}
        >
          <Clock className="w-3.5 h-3.5" />
          {t('dupeReviewLater') || 'Review later'}
        </button>
        <button
          type="button"
          onClick={() => onDismissPermanent?.(pairKey)}
          className="text-xs px-3 py-1.5 rounded-md flex-shrink-0 inline-flex items-center gap-1"
          style={{
            color: 'hsl(125, 12%, 35%)',
            backgroundColor: 'white',
            border: '1px solid hsl(143, 28%, 82%)',
          }}
          data-testid={`duplicate-suggestion-notdupe-${pairKey}`}
          title={t('dupeNotADuplicateTip') || 'These are definitely different plants — never suggest this pair again'}
        >
          <EyeOff className="w-3.5 h-3.5" />
          {t('dupeNotADuplicate') || 'Not a duplicate'}
        </button>
      </div>

      {/* Full-screen side-by-side compare overlay. Mobile: stacks
          vertically (target on top, source below). Desktop: two
          columns. Photos are the largest we could fetch (up to 5 per
          side). Tap backdrop, X, or ESC to close.

          IMPORTANT: portal to document.body. The DuplicateSuggestionCard
          lives inside `header.glassmorphism` which has
          `backdrop-filter: blur(16px)` — per CSS spec that creates a
          containing block for position:fixed descendants, so
          `fixed inset-0` was resolving to the header's box (~388x403)
          instead of the viewport. Result on mobile: hero photos
          clipped, second panel unreachable, scroll wouldn't work
          (iter 89 finding). Portalling to body escapes the header's
          containing block. */}
      {compareOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[80] flex flex-col"
          style={{ backgroundColor: 'rgba(15, 43, 26, 0.94)' }}
          data-testid={`dupe-compare-overlay-${pairKey}`}
          onClick={(e) => {
            // Close on backdrop tap. The overlay's flex children (header,
            // scroll region, footer) tile the whole box, so
            // `e.target === e.currentTarget` never fires — instead, treat
            // any click that isn't inside a panel or on a button/link as
            // a backdrop tap (iter-90 finding).
            const t = e.target;
            if (t.closest && (t.closest('[data-testid^="dupe-compare-panel-"]') || t.closest('button') || t.closest('a') || t.closest('input') || t.closest('[role="tablist"]'))) {
              return;
            }
            setCompareOpen(false);
            setCompareSwapped(false);
          }}
          onTouchStart={onCompareTouchStart}
          onTouchEnd={onCompareTouchEnd}
          role="dialog"
          aria-modal="true"
          aria-label={t('dupeCompareTitle') || 'Compare plants'}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}>
            <div className="min-w-0">
              <div className="text-sm font-semibold" style={{ color: 'white' }}>
                {t('dupeCompareTitle') || 'Compare plants'}
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: 'hsl(143, 20%, 82%)' }}>
                {t('dupeCompareBlurb') || 'Same plant? Tap Merge below. Different plants? Close and dismiss.'}
              </div>
              {/* Mobile-only swipe hint. On desktop we show a button. */}
              <div className="md:hidden text-[10px] italic mt-1" style={{ color: 'hsl(143, 20%, 72%)' }}>
                {compareSwapped
                  ? (t('dupeCompareSwapHintSwapped') || 'Swiped — swipe again to swap back')
                  : (t('dupeCompareSwapHint') || 'Swipe left/right to swap Kept vs Merged in')}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Desktop / accessible swap button — mobile users use swipe. */}
              <button
                type="button"
                onClick={() => setCompareSwapped((v) => !v)}
                className="hidden md:inline-flex items-center gap-1 p-2 rounded-full text-xs"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'white' }}
                aria-label={t('dupeCompareSwap') || 'Swap Kept and Merged in'}
                data-testid={`dupe-compare-swap-${pairKey}`}
                title={t('dupeCompareSwapTip') || 'Swap which plant is Kept vs Merged in'}
              >
                <ArrowRightLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => { setCompareOpen(false); setCompareSwapped(false); }}
                className="p-2 rounded-full"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'white' }}
                aria-label={t('close') || 'Close'}
                data-testid={`dupe-compare-close-${pairKey}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Two-plant grid — stacked on mobile with natural heights
              so the hero photos aren't squeezed by a fixed 50vh split
              (which made them a thin strip when the swap hint added
              a line to the header). On md+ we go side-by-side and
              each panel takes half the viewport. Outer container
              scrolls vertically on mobile. `-webkit-overflow-scrolling`
              via `overscroll-contain` prevents the swipe listener
              from stealing scroll. */}
          <div
            className="flex-1 flex flex-col md:flex-row gap-3 p-3 overflow-y-auto"
            style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
          >
            {(compareSwapped
              ? [
                  { plant: source, photos: compareSource, isTarget: true },
                  { plant: target, photos: compareTarget, isTarget: false },
                ]
              : [
                  { plant: target, photos: compareTarget, isTarget: true },
                  { plant: source, photos: compareSource, isTarget: false },
                ]
            ).map(({ plant, photos, isTarget }) => (
              <div
                key={plant.plant_id}
                className="w-full md:flex-1 min-w-0 rounded-lg overflow-hidden flex flex-col flex-shrink-0"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  border: `2px solid ${isTarget ? 'hsl(143, 30%, 55%)' : 'hsl(21, 70%, 55%)'}`,
                }}
                data-testid={`dupe-compare-panel-${isTarget ? 'target' : 'source'}`}
              >
                {/* Panel header — name + KEEPER / MERGED IN badge. */}
                <div className="px-3 py-2 flex-shrink-0 flex items-center gap-2" style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}>
                  <span
                    className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: isTarget ? 'hsl(143, 28%, 25%)' : 'hsl(21, 70%, 45%)',
                      color: 'white',
                    }}
                  >
                    {isTarget ? (t('mergeKeepThis') || 'Kept') : (t('mergeAbsorbed') || 'Merged in')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate" style={{ color: 'white' }}>{plant.common_name}</div>
                    {plant.scientific_name && (
                      <div className="text-[11px] italic truncate" style={{ color: 'hsl(143, 20%, 82%)' }}>
                        {plant.scientific_name}
                      </div>
                    )}
                  </div>
                </div>
                {/* Photo viewer — one big hero (aspect-contain so
                    nothing is cropped) plus a thumbnail strip below
                    for switching between multiple photos of the same
                    plant. On mobile the panel is naturally sized
                    (not flex-1) so the hero always gets its full
                    height budget rather than being squeezed by a
                    2-way vertical split. */}
                <div className="p-3 flex flex-col md:flex-1">
                  {compareLoading ? (
                    <div className="text-center text-xs pt-6" style={{ color: 'hsl(143, 20%, 82%)' }}>
                      {t('loading') || 'Loading…'}
                    </div>
                  ) : photos.filter(Boolean).length === 0 ? (
                    <div className="text-center text-xs italic pt-6" style={{ color: 'hsl(143, 20%, 78%)' }}>
                      {t('dupePreviewNoPhotos') || 'No photos'}
                    </div>
                  ) : (() => {
                    const validPhotos = photos.filter(Boolean);
                    const active = comparePhotoIdx[isTarget ? 'target' : 'source'] || 0;
                    const safeActive = Math.min(active, validPhotos.length - 1);
                    return (
                      <>
                        <div
                          className="w-full rounded overflow-hidden flex items-center justify-center md:flex-1"
                          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
                        >
                          <img
                            src={validPhotos[safeActive]}
                            alt=""
                            className="w-full h-auto max-h-[50vh] md:max-h-[62vh] object-contain"
                            style={{ minHeight: 200 }}
                            draggable={false}
                          />
                        </div>
                        {validPhotos.length > 1 && (
                          <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1" role="tablist">
                            {validPhotos.map((src, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setComparePhotoIdx((prev) => ({ ...prev, [isTarget ? 'target' : 'source']: i }))}
                                className="w-14 h-14 rounded flex-shrink-0 overflow-hidden"
                                style={{
                                  border: `2px solid ${i === safeActive ? (isTarget ? 'hsl(143, 30%, 55%)' : 'hsl(21, 70%, 55%)') : 'rgba(255,255,255,0.2)'}`,
                                  opacity: i === safeActive ? 1 : 0.7,
                                }}
                                aria-label={`Photo ${i + 1}`}
                                data-testid={`dupe-compare-thumb-${isTarget ? 'target' : 'source'}-${i}`}
                              >
                                <img src={src} alt="" className="w-full h-full object-cover" draggable={false} />
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>

          {/* Sticky footer with actions so the user doesn't have to
              close the overlay just to decide. */}
          <div
            className="flex flex-wrap items-center gap-2 justify-end px-4 py-3 flex-shrink-0"
            style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
          >
            <button
              type="button"
              onClick={() => { setCompareOpen(false); setCompareSwapped(false); onDismissPermanent?.(pairKey); }}
              className="text-xs px-3 py-2 rounded-md inline-flex items-center gap-1"
              style={{ color: 'white', backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)' }}
              data-testid={`dupe-compare-notdupe-${pairKey}`}
            >
              <EyeOff className="w-3.5 h-3.5" />
              {t('dupeNotADuplicate') || 'Not a duplicate'}
            </button>
            <button
              type="button"
              onClick={() => {
                setCompareOpen(false);
                // Respect the swapped role so the merge dialog opens
                // with the user's chosen Kept plant as the target.
                const [tgt, src] = compareSwapped ? [source, target] : [target, source];
                setCompareSwapped(false);
                onMerge?.(tgt, src);
              }}
              className="text-xs font-medium px-4 py-2 rounded-md inline-flex items-center gap-1"
              style={{ backgroundColor: 'hsl(143, 30%, 55%)', color: 'white' }}
              data-testid={`dupe-compare-merge-${pairKey}`}
            >
              <ArrowRightLeft className="w-3.5 h-3.5" />
              {t('mergeAction') || 'Merge'}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
